import "reactflow/dist/style.css";
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import ReactFlow, {
    Background,
    BackgroundVariant,
    Connection,
    ConnectionMode,
    Edge,
    EdgeChange,
    FitViewOptions,
    type Node as FlowNode,
    NodeChange,
    useReactFlow,
    useStore,
    useStoreApi,
    type XYPosition,
} from "reactflow";
import { CanvasControls } from "./CanvasControls";
import {
    CANVAS_TITLE_ATTR,
    fitViewWithMenuOffset,
    fitViewWithMenuOffsetNow,
    setViewportNow,
    topOverlayBottom,
} from "../utils/fitViewWithMenuOffset";
import { computeTranslateExtent } from "../utils/canvasExtent";
import { notebookFlowProps } from "../utils/notebookLayout";
import { useGraphEditGates } from "../hook/useGraphEditGates";
import { NotebookRunAll } from "./notebook/NotebookRunAll";
import { usePosition } from "../hook/usePosition";

import { useFlowContext } from "../providers/FlowProvider";
import { useEditedByOthers } from "../providers/collab/useEditedByOthers";
import { usePackagePalette } from "../providers/packages/PackagePaletteContext";
import { useToastContext } from "../providers/ToastProvider";
import {
    useDatasetDetails,
    viewDatasetDetailsToast,
} from "./datasets/catalog/datasetDetailsContext";
import { packageKeyFromCanonicalNodeType } from "../registry/packageKeys";
import { NodeType, EdgeType, CURIO_UNIVERSAL_NODE_TYPE } from "../constants";
import { getFlowNodeCanonicalType } from "../utils/flowNodeCanonicalType";
import { DEFAULT_DELETE_KEY_CODES } from "./canvasKeyBindings";
import { useRunSelectedNodeShortcut } from "../hook/useRunSelectedNodeShortcut";
import { useViewportMotionHint } from "../hook/useViewportMotionHint";
import UniversalNode from "./UniversalNode";
import BiDirectionalEdge from "./edges/BiDirectionalEdge";
import { useCode } from "../hook/useCode";
import { ToolsMenu, UpMenu } from "components/menus";
import UniDirectionalEdge from "./edges/UniDirectionalEdge";
import "./MainCanvas.css";
import VersionBadge from "./VersionBadge";
import {
    buildDatasetLoaderNodeOptions,
    buildDatasetWidgetNodeOptions,
    hasDatasetDrag,
    readDatasetDragPayload,
    type DatasetDragPayload,
} from "../services/datasetCatalog";
import { hasFileDrag, useDataFileImport } from "./datasets/useDataFileImport";
import { useAskTheOwner } from "./datasets/useAskTheOwner";
import { useAskForPackage } from "./packages/useAskForPackage";
import {
    hasModelDrag,
    packageCardNodeOptions,
    readModelDragPayload,
    type ModelDragPayload,
} from "../services/packages/modelApplication";
import {
    endPackageCardDrag,
    hasPackageCardDrag,
    readPackageCardDragPayload,
    type PackageCardNode,
} from "../services/packages/packageCardDrag";
import { usePackageCardDrop, type PackageCardDrop } from "./packages/usePackageCardDrop";
import { useHostedGuest } from "./apiSettings/useHostedGuest";
import {
    endScenarioDrag,
    hasScenarioDrag,
    readScenarioDragPayload,
    type ScenarioDragPayload,
} from "../services/scenarioCatalog/scenarioDrag";
import { useScenarioDrop } from "./scenarios/useScenarioDrop";
import { packageStarterCode } from "../adapters/node/packageNodeBehavior";
import { useStarterContext } from "../providers/StarterProvider";
import { tryGetNodeDescriptor } from "../registry/nodeRegistry";
import {
  agentsApi,
  agentsOnCanvas,
  readAgentDragCoord,
  notifyAgentDockRefresh,
  resolveAgentDropTarget,
  hasAgentDrag,
  type AgentDropTarget,
} from "../services/agents";
import { clearAgentDropHover, setAgentDropHoverEdgeId } from "../utils/agentDropHover";
import { attachAgentOnDrop } from "../utils/agentDropAttach";
import { AgentDockOverlay } from "./agents/attach/AgentDockOverlay";
import { AgentDock } from "./agents/attach/AgentDock";
import { AgentAttachmentsProvider, useAgentsDataflowId } from "../providers/agents";
import { isDrawnHidden } from "../utils/hiddenNodes";
import { isNodeDragRegion, keepPaneOffNodes } from "../utils/nodeDragRegion";
import { useSharedView } from "../hook/useSharedView";
import { useCollab } from "../providers/CollaborationProvider";
import { useDocBinding } from "../providers/collab/docContext";
import { frameNodesInView, revealRectInView } from "../utils/focusDatasetNodes";
import { scenarioCanvasView } from "../utils/scenarios/scenarioCanvasView";
import { BOX_WIDTH, boxLayout } from "./scenarios/ScenarioLayers";
import { CanvasScenarioLayers } from "./scenarios/CanvasScenarioLayers";
import { useScenarioUi } from "./scenarios/scenarioUi";
import { useProjectLoadState } from "./projectLoadState";
import { useDropsWhileOpening } from "../hook/useDropsWhileOpening";

/** What a drop on the canvas carries, read while its event lasts: the browser
 *  keeps a drag's data only for its drop, and a card's own drag lets its
 *  payload go at its end. */
type CanvasDrop =
    | { kind: "dataset"; dataset: DatasetDragPayload; position: XYPosition }
    | { kind: "files"; files: File[]; position: XYPosition }
    | { kind: "package"; dropped: PackageCardDrop }
    | { kind: "model"; model: ModelDragPayload }
    | { kind: "scenario"; scenario: ScenarioDragPayload; position: XYPosition }
    | { kind: "agent"; agentCoord: string; target: AgentDropTarget }
    | { kind: "node"; type: NodeType; position: XYPosition };

const FILL_STYLE: React.CSSProperties = { width: "100%", height: "100%" };
// The notebook is a white page, as a Jupyter notebook is, under its cells.
const NOTEBOOK_SCROLLER_STYLE: React.CSSProperties = {
    width: "100%",
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    backgroundColor: "#ffffff",
};
// React Flow's own view in the notebook view, where the page scrolls instead.
const NOTEBOOK_VIEWPORT = { x: 0, y: 0, zoom: 1 };

export function MainCanvas() {
    const { showToast } = useToastContext();
    const { openDatasetDetails } = useDatasetDetails();
    const { setActivePackageKey } = usePackagePalette();
    const {
        nodes,
        edges,
        loading,
        onNodesChange,
        onEdgesChange,
        onConnect,
        isValidConnection,
        onEdgesDelete,
        onNodesDelete,
        markDirty,
        requestAutoRun,
        ensureProjectId,
        whenChangesKept,
        notebookOn,
        notebookContentHeight,
        setNotebookPane,
        registerNotebookScroller,
        revealNodes,
        scenarios,
        packages: dataflowPackages,
        viewerMode,
        dataflowDoc,
        applyRemoveChanges,
    } = useFlowContext();
    // The canvas's binding to the dataflow's doc, read when a change lands.
    const dataflowDocRef = useRef(dataflowDoc);
    dataflowDocRef.current = dataflowDoc;
    // The nodes someone else is editing: removing one asks first.
    const editedByOthers = useEditedByOthers();
    const hostedGuest = useHostedGuest();

    // The scenario pointed at in the Scenario Catalog drawer's In project tab
    // (#662), whose fixed context the canvas marks. Never saved.
    const { highlighted: highlightedScenario } = useScenarioUi();

    // What React Flow draws: the flow's own nodes and edges, with collapsed
    // scenarios hidden and their members marked, plus the boxes, frames and
    // stand-in edges the scenario layers draw beside it. The flow's state
    // itself never holds any of it (#662). The notebook view shows every node
    // as a cell: a hidden member would leave an empty slot, and the boxes and
    // frames are placed by canvas positions the cells do not have.
    const scenarioView = useMemo(
        () => scenarioCanvasView(nodes, edges, notebookOn ? [] : scenarios ?? [], { fixedFor: highlightedScenario }),
        [nodes, edges, scenarios, highlightedScenario, notebookOn],
    );

    // How far the viewport may pan, tracking the nodes rather than a fixed box
    // (#234). Two memos on purpose: React Flow re-applies `translateExtent`
    // through an effect keyed on the value's IDENTITY, so handing it a fresh
    // array every render would call `d3Zoom.translateExtent()` on every frame
    // of a drag. `computeTranslateExtent` rounds to a coarse grid, and keying
    // the tuple on those four numbers keeps the identity stable until a node
    // actually crosses a boundary. A collapsed scenario's box counts as a node.
    const [extentMinX, extentMinY, extentMaxX, extentMaxY] = useMemo(() => {
        const boxes = scenarioView.boxes.map((box) => ({
            id: box.scenario.id,
            position: { x: box.x, y: box.y },
            positionAbsolute: { x: box.x, y: box.y },
            width: BOX_WIDTH,
            height: boxLayout(box).height,
            data: {},
        }));
        const [[minX, minY], [maxX, maxY]] = computeTranslateExtent(
            boxes.length > 0 ? [...nodes, ...boxes] : nodes,
        );
        return [minX, minY, maxX, maxY];
    }, [nodes, scenarioView.boxes]);
    const translateExtent = useMemo(
        () =>
            [
                [extentMinX, extentMinY],
                [extentMaxX, extentMaxY],
            ] as [[number, number], [number, number]],
        [extentMinX, extentMinY, extentMaxX, extentMaxY],
    );

    const { createCodeNode } = useCode();

    // One stable RF type avoids remounting editors when ``loadInstalledPackages`` re-registers manifests.
    const nodeTypes = useMemo(
        () => ({ [CURIO_UNIVERSAL_NODE_TYPE]: UniversalNode }),
        [],
    );

    const edgeTypes = useMemo(() => ({
        [EdgeType.BIDIRECTIONAL_EDGE]: BiDirectionalEdge,
        [EdgeType.UNIDIRECTIONAL_EDGE]: UniDirectionalEdge,
    }), []);

    const reactFlow = useReactFlow();
    const { setViewport, screenToFlowPosition } = useReactFlow();
    const viewportMotionHint = useViewportMotionHint();

    // The notebook view holds React Flow on its own pane: zoom 1, no gestures,
    // and a translate extent equal to the pane, so the page scrolls instead.
    // Memoized on the pane's size, as `translateExtent` is above, because
    // React Flow re-applies the extent whenever its identity changes.
    const flowWidth = useStore((s) => s.width);
    const flowHeight = useStore((s) => s.height);
    const notebookProps = useMemo(
        () => (notebookOn ? notebookFlowProps(flowWidth, flowHeight) : null),
        [notebookOn, flowWidth, flowHeight],
    );
    // In the notebook view the page scrolls and React Flow's own view stays at
    // the origin. The settings above stop gestures but not a call that sets
    // the view (a load's fit, a framing helper), so a view that moves is put
    // back. React Flow 11 lands `setViewport` a frame or two later, through a
    // d3 transition, so the check runs on every change of the view. It also
    // runs again when `setViewport` changes, which it does once React Flow's
    // zoom is ready: before that, it does nothing.
    //
    // The view is put back at once (`setViewportNow`), not through
    // `setViewport`: on a page that drew no frame for a while, a fit's view
    // stayed until one came (#996). A change is put back once the call that
    // made it has returned, in a microtask: setting d3's zoom from inside its
    // own zoom event would end a transition in its last tick without ending
    // its gesture. Only while the hold is on, so nothing it asked for lands
    // after the view is left; useNotebookView then sets the canvas view back
    // at once.
    const flowStore = useStoreApi();
    useEffect(() => {
        if (!notebookOn) return;
        let holding = true;
        const hold = () => {
            const [x, y, zoom] = flowStore.getState().transform;
            if (!holding || (x === 0 && y === 0 && zoom === 1)) return;
            if (!setViewportNow(flowStore, NOTEBOOK_VIEWPORT)) setViewport(NOTEBOOK_VIEWPORT);
        };
        hold();
        const unsubscribe = flowStore.subscribe((state, prev) => {
            if (state.transform !== prev.transform) queueMicrotask(hold);
        });
        return () => {
            holding = false;
            unsubscribe();
        };
    }, [notebookOn, flowStore, setViewport]);

    // The element the notebook scrolls in. Its size and the overlays fixed over
    // its top (top bar, title chips) decide where the cells go. The notebook
    // view has no palette rail (its (+) opens the rail as a row), so the cells
    // run from the left margin.
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        registerNotebookScroller(scroller);
        const measure = () => {
            const rect = scroller.getBoundingClientRect();
            const top = topOverlayBottom();
            setNotebookPane({
                width: scroller.clientWidth,
                top: top === null ? 0 : Math.max(0, top - rect.top),
                left: 0,
            });
        };
        measure();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
        observer?.observe(scroller);
        const title = document.querySelector(`[${CANVAS_TITLE_ATTR}]`);
        if (title) observer?.observe(title);
        window.addEventListener("resize", measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", measure);
            registerNotebookScroller(null);
        };
    }, [loading, registerNotebookScroller, setNotebookPane]);

    // Where a dropped node goes. On the canvas, under the pointer. In the
    // notebook view the pointer is on a page, not on the canvas, so the node
    // takes the next free canvas spot and its cell is scrolled into view.
    const { getPosition } = usePosition();
    const dropPosition = useCallback(
        (event: React.DragEvent) =>
            notebookOn ? getPosition() : screenToFlowPosition({ x: event.clientX, y: event.clientY }),
        [notebookOn, getPosition, screenToFlowPosition],
    );
    const revealCreated = useCallback((node: { id: string } | undefined | void) => {
        if (node && node.id) revealNodes([node.id]);
    }, [revealNodes]);

    // Test hook: expose the ReactFlow instance and a menu-aware fitView so
    // Playwright can force the same shifted viewport the in-app loader uses
    // (see useWorkflowOperations.ts) before taking screenshots. A fit without
    // a duration measures the nodes React Flow has not measured yet, is set at
    // once and returns the viewport it set (null while a node has no size), so
    // a helper can wait for that viewport without an animation frame (#1012).
    // Kept unconditional (read-only from the outside and cheap), so e2e tests
    // don't need a separate build flag.
    useEffect(() => {
        (window as any).__curio_reactFlow = reactFlow;
        (window as any).__curio_fitViewWithMenuOffset = (options?: FitViewOptions) =>
            options?.duration
                ? fitViewWithMenuOffset(reactFlow, options)
                : fitViewWithMenuOffsetNow(reactFlow, flowStore, options);
        return () => {
            if ((window as any).__curio_reactFlow === reactFlow) {
                delete (window as any).__curio_reactFlow;
                delete (window as any).__curio_fitViewWithMenuOffset;
            }
        };
    }, [reactFlow, flowStore]);

    // Test hook, as the one above: this tab's presence in the dataflow's room
    // (its doc's awareness), so Playwright can read what reached it and say
    // where this tab points, as another tab's canvas would.
    const awareness = useDocBinding()?.provider.awareness ?? null;
    useEffect(() => {
        if (!awareness) return;
        (window as any).__curio_awareness = awareness;
        return () => {
            if ((window as any).__curio_awareness === awareness) delete (window as any).__curio_awareness;
        };
    }, [awareness]);

    // Ctrl/Cmd+Enter on a selected node (#223).
    useRunSelectedNodeShortcut();

    // A read-only canvas: a watcher's, on another account's dataflow opened
    // from its link (``viewerMode === "shared"``). An editor the owner
    // invited edits it: their changes reach the dataflow's doc, which the
    // server keeps (useSharedView).
    const isSharedView = useSharedView();
    // The owner and the editors the owner invited work with the dataflow's
    // agents, and a watcher with none; on an editor's canvas an install an
    // agent proposes waits for the owner's review.
    const agents = agentsOnCanvas(isSharedView ? "watcher" : viewerMode === "shared" ? "editor" : "owner");
    // The dataflow whose agents they work with: on an editor's canvas, the
    // one its link opened, since the flow state names none there.
    const agentsDataflow = useAgentsDataflowId();
    // The role the dataflow's room gives this tab, once its join answered.
    const { role: roomRole } = useCollab();
    // How far the load of the dataflow the route names has got: what is
    // dropped before it is in waits for it (useDropsWhileOpening).
    const loadState = useProjectLoadState();
    // Nodes are added (dropped), connected and deleted, and connections
    // removed, on the canvas only, by whoever edits it; the notebook view
    // changes no graph. A node's Delete node tool reads the same rule.
    const graphEdits = useGraphEditGates();

    // React Flow gives a node that drags the `nopan` class, which keeps its
    // pane's gestures off the node: a press there does not pan the view and a
    // double-click does not zoom it 2x at the pointer. A read-only canvas's
    // nodes do not drag, so they get the class here, and the double-click
    // below reaches them as it reaches an editable canvas's nodes.
    // A node someone else is editing is not deleted by the Delete key: the key
    // asks first (below).
    const drawnNodes = useMemo(() => {
        const drawn = isSharedView && !notebookOn ? keepPaneOffNodes(scenarioView.nodes) : scenarioView.nodes;
        if (editedByOthers.size === 0) return drawn;
        return drawn.map((node) => (editedByOthers.has(node.id) ? { ...node, deletable: false } : node));
    }, [isSharedView, notebookOn, scenarioView.nodes, editedByOthers]);

    // The Delete key on a selected node someone else is editing: "Ana is
    // editing this node. Remove it anyway?" The others selected go at once,
    // through React Flow's own delete.
    useEffect(() => {
        if (!graphEdits.delete || editedByOthers.size === 0) return;
        const onKey = (event: KeyboardEvent) => {
            if (!DEFAULT_DELETE_KEY_CODES.includes(event.key)) return;
            const target = event.target as HTMLElement | null;
            if (target && (target.closest("input, textarea, select, [contenteditable='true'], .monaco-editor"))) return;
            const asked = reactFlow.getNodes().filter((node) => node.selected && editedByOthers.has(node.id));
            if (asked.length === 0) return;
            applyRemoveChanges(asked.map((node) => ({ id: node.id, type: "remove" as const })));
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [graphEdits.delete, editedByOthers, reactFlow, applyRemoveChanges]);

    // A double-click where a press drags a node (its header, its card's edge)
    // zooms the view onto it, framed as a focus frames its nodes, on an
    // editable canvas and on a read-only one, where a node would drag if it
    // could. Inside an editor, an output, a chart or a map the double-click
    // stays theirs. The notebook view keeps its zoom at 1.
    const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: FlowNode) => {
        if (notebookOn) return;
        if (!isNodeDragRegion(event.target, event.currentTarget)) return;
        frameNodesInView(reactFlow, [node.id]);
    }, [notebookOn, reactFlow]);

    // Last dragover point, so a pointer that reports the same coordinate twice
    // (browsers fire dragover on a timer as well as on movement) costs nothing.
    const lastDragPointRef = useRef<{ x: number; y: number } | null>(null);

    const handleDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        // Dataset, model, package card, scenario AND agent drags use
        // effectAllowed="copy"; a "move" dropEffect is an incompatible pair, so
        // the browser cancels the drop (handleDrop never fires and the agent
        // silently fails to attach). Node-creation drags keep "move".
        const wantsCopy =
            hasDatasetDrag(event.dataTransfer) ||
            hasFileDrag(event.dataTransfer) ||
            hasModelDrag(event.dataTransfer) ||
            hasPackageCardDrag(event.dataTransfer) ||
            hasScenarioDrag(event.dataTransfer) ||
            hasAgentDrag(event.dataTransfer);
        event.dataTransfer.dropEffect = wantsCopy ? "copy" : "move";

        // Tell the edges which connection would receive this drop (#296). Only
        // for agent drags: a dataset or node-creation drag pays nothing, which
        // is what keeps this off the hot path for every other kind of drag.
        if (!hasAgentDrag(event.dataTransfer)) {
            clearAgentDropHover();
            return;
        }
        const last = lastDragPointRef.current;
        if (last && last.x === event.clientX && last.y === event.clientY) return;
        lastDragPointRef.current = { x: event.clientX, y: event.clientY };
        const target = resolveAgentDropTarget({
            // A collapsed scenario's members keep their old size while hidden.
            nodes: reactFlow.getNodes().filter((n) => !isDrawnHidden(n)),
            flowPoint: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
            clientX: event.clientX,
            clientY: event.clientY,
        });
        setAgentDropHoverEdgeId(target.kind === "connection" ? target.targetId : null);
    }, [reactFlow, screenToFlowPosition]);

    const handleDragLeave = useCallback((event: React.DragEvent) => {
        // dragleave bubbles from descendants, so a pointer crossing from the
        // pane onto a node fires one here with relatedTarget still inside the
        // drop target. Only a relatedTarget outside it - or null, which is how
        // leaving the window reports - is a real exit.
        const next = event.relatedTarget as Node | null;
        if (next && event.currentTarget.contains(next)) return;
        lastDragPointRef.current = null;
        clearAgentDropHover();
    }, []);

    // The end of a drag that never reaches the canvas: Escape cancels it, the
    // pointer is released over another application, or the drop lands outside
    // the window. None of those fire dragleave on the pane, and dragend fires on
    // the drag SOURCE, so the listener has to be global. Capture phase so
    // nothing downstream can swallow it.
    useEffect(() => {
        const clear = () => {
            lastDragPointRef.current = null;
            clearAgentDropHover();
        };
        window.addEventListener("dragend", clear, true);
        window.addEventListener("drop", clear, true);
        return () => {
            window.removeEventListener("dragend", clear, true);
            window.removeEventListener("drop", clear, true);
        };
    }, []);

    // A dataset dropped on the empty canvas, a Data Catalog card or a file just
    // imported, becomes a Data Loading node holding it: the node a drop on a
    // fresh Data Loading node makes, its Data widget set to the dataset and its
    // template's starter as its code. Like a value set in that widget, it runs
    // when automatic runs are on.
    const { getStarters } = useStarterContext();
    const createLoaderFor = useCallback((dataset: DatasetDragPayload, position: XYPosition): string | undefined => {
        const descriptor = tryGetNodeDescriptor(NodeType.DATA_LOADING);
        const options =
            buildDatasetWidgetNodeOptions(dataset, position, descriptor?.widgets, packageStarterCode(descriptor, getStarters))
            ?? buildDatasetLoaderNodeOptions(dataset, position);
        const created = createCodeNode(NodeType.DATA_LOADING, options);
        revealCreated(created);
        showToast(
            `Created a Data Loading node for ${dataset.title}.`,
            "success",
            viewDatasetDetailsToast(openDatasetDetails, dataset.datasetId),
        );
        markDirty();
        // A node still waiting for its starter has nothing to run yet.
        if (created && options.code !== undefined) requestAutoRun?.([created.id]);
        return created?.id;
    }, [getStarters, revealCreated, createCodeNode, markDirty, showToast, openDatasetDetails, requestAutoRun]);

    // On the owner's dataflow, an invited editor's card of his own Data
    // Catalog asks her to add it, as his file drop and his Add to project do
    // (useAskTheOwner): a dataset she lists, or one of the shared catalog,
    // asks nothing.
    const askTheOwner = useAskTheOwner();

    // A file from the computer dropped on the empty canvas: imported into the
    // Data Catalog and this dataflow, then made a Data Loading node as a card is.
    const importDataFile = useDataFileImport();

    // A package's card from the Node Catalog drawer becomes one of the
    // package's nodes: its one node, or the one picked from a menu of its
    // nodes at the drop point (usePackageCardDrop). A model's card from the
    // Model Catalog drawer or the Models palette is its package's card: its
    // one node runs the model, opening with its template's code, which names
    // the model. A package the dataflow has not added goes through the
    // drawer's own Add first (the review, then the install of its libraries),
    // and the node is made once it is added; the Models palette lists only
    // the dataflow's models, so its rows make their nodes at once. A hosted
    // guest's drop is refused, since guests never install. On the owner's
    // dataflow an invited editor's drop of a package the dataflow does not use
    // makes its node at once and asks her to add the package
    // (useAskForPackage): the node waits for her review.
    const createPackageNode = useCallback((node: PackageCardNode, position: XYPosition): string | undefined => {
        const starter = node.model
            ? packageStarterCode(tryGetNodeDescriptor(node.nodeType as NodeType), getStarters)
            : undefined;
        const created = createCodeNode(node.nodeType as NodeType, { position, ...packageCardNodeOptions(node, starter) });
        revealCreated(created);
        const article = /^[aeiou]/i.test(node.label) ? "an" : "a";
        showToast(
            node.model
                ? `Created ${article} ${node.label} node for ${node.model.name}.`
                : `Created ${article} ${node.label} node.`,
            "success",
        );
        markDirty();
        return created?.id;
    }, [getStarters, revealCreated, createCodeNode, showToast, markDirty]);
    const askForPackage = useAskForPackage();
    const { drop: dropPackageCard, menu: packageNodeMenu } = usePackageCardDrop({
        dataflowPackages,
        hostedGuest,
        createNode: createPackageNode,
        showToast,
        askTheOwner: askForPackage,
    });
    // A scenario from the Scenario Catalog arrives as a copy: its box where it
    // was dropped, its context as fixed data (useScenarioDrop). The notebook
    // view shows its cells. On the canvas the box is drawn beside React Flow,
    // not in its store, so no fit of nodes frames it: once it is drawn, the
    // view moves as far as it takes to bring the box into sight (#769).
    const dropScenario = useScenarioDrop();
    const [boxToReveal, setBoxToReveal] = useState<string | null>(null);
    useEffect(() => {
        if (!boxToReveal) return;
        const box = scenarioView.boxes.find((b) => b.scenario.id === boxToReveal);
        if (!box) return;
        setBoxToReveal(null);
        revealRectInView(reactFlow, { x: box.x, y: box.y, width: BOX_WIDTH, height: boxLayout(box).height });
    }, [boxToReveal, scenarioView.boxes, reactFlow]);

    // What a drop on the canvas carries, read while its event lasts, and where
    // it was dropped, each kind told apart in the order it always was: a
    // dataset, a file, a package's card, a model, a scenario, an agent, then
    // a node's tile.
    const readDrop = useCallback((event: React.DragEvent): CanvasDrop | null => {
        const data = event.dataTransfer;
        if (hasDatasetDrag(data)) {
            event.preventDefault();
            event.stopPropagation();
            const dataset = readDatasetDragPayload(data);
            return dataset ? { kind: "dataset", dataset, position: dropPosition(event) } : null;
        }
        if (hasFileDrag(data)) {
            event.preventDefault();
            event.stopPropagation();
            return { kind: "files", files: Array.from(data.files || []), position: dropPosition(event) };
        }
        if (hasPackageCardDrag(data)) {
            event.preventDefault();
            event.stopPropagation();
            const card = readPackageCardDragPayload(data);
            endPackageCardDrag();
            if (!card) return null;
            return { kind: "package", dropped: { card, position: dropPosition(event), at: { x: event.clientX, y: event.clientY } } };
        }
        if (hasModelDrag(data)) {
            event.preventDefault();
            event.stopPropagation();
            const model = readModelDragPayload(data);
            return model ? { kind: "model", model } : null;
        }
        if (hasScenarioDrag(data)) {
            event.preventDefault();
            event.stopPropagation();
            const scenario = readScenarioDragPayload(data);
            endScenarioDrag();
            return scenario ? { kind: "scenario", scenario, position: dropPosition(event) } : null;
        }
        const agentCoord = readAgentDragCoord(data);
        if (agentCoord) {
            event.preventDefault();
            clearAgentDropHover();
            lastDragPointRef.current = null;
            // Node first, then edge, then canvas, hit-tested against node
            // geometry (reliable regardless of which DOM layer received the
            // drop) and then the DOM for edges. The SAME resolver feeds the
            // drag-over highlight, so what lights up under the pointer and what
            // actually receives the drop cannot disagree (#296).
            const target: AgentDropTarget = resolveAgentDropTarget({
                nodes: reactFlow.getNodes().filter((n) => !isDrawnHidden(n)),
                flowPoint: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
                clientX: event.clientX,
                clientY: event.clientY,
            });
            return { kind: "agent", agentCoord, target };
        }
        event.preventDefault();
        const type = data.getData("application/reactflow") as NodeType;
        return type ? { kind: "node", type, position: dropPosition(event) } : null;
    }, [dropPosition, reactFlow, screenToFlowPosition]);

    // What a drop makes on the canvas.
    const landDrop = useCallback((drop: CanvasDrop) => {
        switch (drop.kind) {
            case "dataset": {
                const nodeId = createLoaderFor(drop.dataset, drop.position);
                if (askTheOwner) void askTheOwner(drop.dataset, { nodeId });
                return;
            }
            case "files":
                void importDataFile(drop.files).then((dataset) => {
                    if (dataset) createLoaderFor(dataset, drop.position);
                });
                return;
            case "package":
                dropPackageCard(drop.dropped);
                return;
            case "model":
                // A model dragged onto the empty canvas is its package's card
                // (it carries both drags): the card drop makes its package's
                // node. Only a model whose package has no node at all gets here.
                showToast(`${drop.model.name} has no node to add.`, "warning");
                return;
            case "scenario":
                void dropScenario(drop.scenario, drop.position).then((dropped) => {
                    if (!dropped || revealNodes(dropped.nodes)) return;
                    setBoxToReveal(dropped.scenario);
                });
                return;
            case "agent": {
                const { agentCoord, target } = drop;
                const where =
                    target.kind === "node"
                        ? "the node"
                        : target.kind === "connection"
                            ? "the connection"
                            : "the canvas";
                // For a node target, the server holds the (possibly freshly-added)
                // node before the attach is checked against the dataflow:
                // otherwise the attach 400s. See attachAgentOnDrop.
                attachAgentOnDrop({
                    projectId: agentsDataflow,
                    target,
                    agentCoord,
                    keepDataflow: ensureProjectId,
                    attach: (pid, coord, t) => agentsApi.attach(pid, coord, t),
                    // A hosted guest's drop that would save says to sign in.
                    hostedGuest,
                    // An invited editor's tab makes no dataflow: it waits for the
                    // server to have what the editor changed, then attaches.
                    savesHere: viewerMode !== "shared",
                    whenKept: whenChangesKept,
                })
                    .then(() => {
                        notifyAgentDockRefresh();
                        markDirty();
                        showToast(`Agent attached to ${where}.`, "success");
                    })
                    // A refusal says how to show itself (the sign-in note is a warning).
                    .catch((e: any) => showToast(e?.message || "Attach failed.", e?.toastVariant ?? "error"));
                return;
            }
            case "node":
                revealCreated(createCodeNode(drop.type, { position: drop.position }));
                markDirty();
        }
    }, [createLoaderFor, askTheOwner, importDataFile, dropPackageCard, showToast, dropScenario, revealNodes, agentsDataflow, ensureProjectId, hostedGuest, viewerMode, whenChangesKept, markDirty, revealCreated, createCodeNode]);

    // While the dataflow the route names is still opening, a drop waits for
    // it, then is made by the canvas it opened as: the load would replace
    // what a drop made before it, and until the dataflow is in, the canvas
    // takes the owner's way, which refused an invited editor's file as the
    // dataflow still opening. A canvas that opens read-only lets it go.
    const keepWhileOpening = useDropsWhileOpening<CanvasDrop>(
        { load: loadState, viewerMode, roomRole },
        (drop) => {
            if (graphEdits.drop) landDrop(drop);
        },
    );

    const handleDrop = useCallback((event: React.DragEvent) => {
        const drop = readDrop(event);
        if (drop && !keepWhileOpening(drop)) landDrop(drop);
    }, [readDrop, keepWhileOpening, landDrop]);

    // The Delete key reaches these through React Flow, which sends the
    // selected edges plus every edge attached to a deleted node first, then
    // the nodes (#155). Both are applied as sent.
    const handleNodesChange = useCallback((changes: NodeChange[]) => {
        let dirty = false;

        for (const change of changes) {
            if (change.type === "remove") dirty = true;

            if (
                change.type === "position" &&
                change.position != undefined &&
                change.position.x != undefined
            ) {
                dirty = true;
            }
        }

        // A drag shows live in the others' tabs; its place is written at the drop.
        dataflowDocRef.current?.noteDrag(changes);
        if (dirty) markDirty();
        onNodesDelete(changes);
        return onNodesChange(changes);
    }, [onNodesDelete, onNodesChange, markDirty]);

    // A connection can move its target further down the notebook's column; the
    // view follows it there rather than leaving the cell to vanish off screen.
    const handleConnect = useCallback((connection: Connection) => {
        onConnect(connection);
        if (connection.target) revealNodes([connection.target], { ifMoved: true });
    }, [onConnect, revealNodes]);

    const handleEdgesChange = useCallback((changes: EdgeChange[]) => {
        if (changes.some((change) => change.type === "remove")) markDirty();
        return onEdgesChange(changes);
    }, [onEdgesChange, markDirty]);

    const handleEdgesDelete = useCallback((edges: Edge[]) => {
        if (edges.length > 0) markDirty();
        return onEdgesDelete(edges);
    }, [onEdgesDelete, markDirty]);

    const handleSelectionChange = useCallback((selection: { nodes: any[]; edges: any[] }) => {
        const packageKey = selection.nodes
            .map((n) => packageKeyFromCanonicalNodeType(getFlowNodeCanonicalType(n)))
            .find((k): k is string => k != null);
        setActivePackageKey(packageKey ?? null);
    }, [setActivePackageKey]);

    // A Shift-drag on the empty canvas draws React Flow's selection box, and
    // the browser would also select the text of every node and panel the
    // pointer crosses. So text selection is refused from the press until the
    // button comes up, wherever the pointer is by then, as a drag that pans
    // the canvas refuses it (d3's dragDisable); the press itself goes on as
    // before. Text selected before the box goes too: the box selects nodes.
    const handleSelectionStart = useCallback(() => {
        window.getSelection()?.removeAllRanges();
        const refuseTextSelection = (event: Event) => event.preventDefault();
        window.addEventListener("selectstart", refuseTextSelection, true);
        window.addEventListener(
            "mouseup",
            () => window.removeEventListener("selectstart", refuseTextSelection, true),
            { capture: true, once: true },
        );
    }, []);

    const loadingAnimation = () => {
        return <div id="plug-loader" role="status" aria-live="polite" aria-busy="true">
                <style>{`
                    #plug-loader {
                    position: fixed;
                    inset: 0;
                    background: #000;             
                    display: grid;                 
                    place-items: center;      
                    z-index: 9999;               
                    }
                    #plug-loader .spinner {
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    border: 6px solid rgba(255,255,255,0.15);
                    border-top-color: #fff;        /* visible on black */
                    animation: plug-rotate 0.9s linear infinite;
                    }
                    @keyframes plug-rotate {
                    to { transform: rotate(360deg); }
                    }
                    #plug-loader .sr-only {
                    position: absolute;
                    width: 1px; height: 1px;
                    padding: 0; margin: -1px;
                    overflow: hidden; clip: rect(0,0,1px,1px);
                    white-space: nowrap; border: 0;
                    }
                `}</style>
                <div className="spinner" />
                <span className="sr-only">Loading…</span>
            </div>
    }

    return (
        <AgentAttachmentsProvider enabled={agents.drives} installsWaitForOwner={agents.installsWaitForOwner}>
        {!loading ? <div
            style={{ width: "100vw", height: "100vh", backgroundColor: "#f0f0f0" }}
            data-curio-room-role={roomRole ?? undefined}
        >
            {/* The notebook view adds no nodes, so it has no rail, only the
                rail's Run all. */}
            {!notebookOn ? <ToolsMenu /> : <NotebookRunAll />}
            <UpMenu />
            <div
                className="curio-canvas-drop-target"
                style={{ width: "100%", height: "100%" }}
                onDragOver={graphEdits.drop ? handleDragOver : undefined}
                onDragLeave={graphEdits.drop ? handleDragLeave : undefined}
                onDrop={graphEdits.drop ? handleDrop : undefined}
            >
            {/* Present in both views so switching never remounts React Flow:
                on the canvas both fill the window and change nothing; in the
                notebook view the outer one scrolls and the inner one is as
                tall as the column. React Flow always fills its parent (its own
                100% size wins over a `style` passed to it), so the height goes
                on the parent. */}
            <div
                ref={scrollerRef}
                className="curio-flow-scroller"
                data-curio-notebook={notebookOn ? "true" : undefined}
                style={notebookOn ? NOTEBOOK_SCROLLER_STYLE : FILL_STYLE}
            >
            <div
                className="curio-flow-sizer"
                style={notebookOn ? { position: "relative", width: "100%", height: notebookContentHeight, minHeight: "100%" } : FILL_STYLE}
            >
            <ReactFlow
                nodes={drawnNodes}
                edges={scenarioView.edges}
                onNodesChange={handleNodesChange}
                onEdgesChange={handleEdgesChange}
                onEdgesDelete={handleEdgesDelete}
                selectionKeyCode={"Shift"}
                panActivationKeyCode={null}
                onSelectionChange={handleSelectionChange}
                onSelectionStart={handleSelectionStart}
                onNodeDoubleClick={handleNodeDoubleClick}
                onConnect={graphEdits.connect ? handleConnect : undefined}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                isValidConnection={isValidConnection}
                connectionMode={ConnectionMode.Loose}
                minZoom={0.05}
                translateExtent={translateExtent}
                onMoveStart={viewportMotionHint.onMoveStart}
                onMove={viewportMotionHint.onMove}
                onMoveEnd={viewportMotionHint.onMoveEnd}
                nodesDraggable={!isSharedView}
                elementsSelectable={true}
                // Set here, not left to React Flow's default: it keeps a value
                // the notebook view's props set until another one replaces it.
                edgesFocusable={true}
                nodesConnectable={graphEdits.connect}
                edgesUpdatable={graphEdits.connect}
                // React Flow defaults to "Backspace" alone, so Windows users pressing
                // Delete got no response (#153). useKeyPress bails on isInputDOMNode,
                // so neither key can fire while the caret is in Monaco or an input.
                // No key deletes in the notebook view or on a read-only canvas.
                deleteKeyCode={graphEdits.delete ? DEFAULT_DELETE_KEY_CODES : null}
                {...(notebookProps ?? {})}
                // The version badge owns the bottom-right corner, and the
                // attribution drawn there sat under it (#509). The other three
                // canvases already hide it.
                proOptions={{ hideAttribution: true }}
            >
                {!notebookOn && <Background color="#a0a0a0" variant={BackgroundVariant.Dots} gap={20} size={2} />}
                {!notebookOn && <CanvasControls />}
                {!notebookOn && <CanvasScenarioLayers view={scenarioView} editable={!isSharedView} />}
            </ReactFlow>
            </div>
            </div>
            {/* Who is here: on the canvas of whoever drives the dataflow's
                agents, the owner's or an editor's, with the agents and their
                chats; on a watcher's only the people. */}
            {agents.drives ? <AgentDockOverlay /> : <AgentDock belowBanner={isSharedView} />}
            </div>

        </div> : loadingAnimation() }
        {packageNodeMenu}
        <VersionBadge />
        </AgentAttachmentsProvider>
    );
}
